//src/pages/GalleryPage

import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../api/api";
import Spinner from "../components/common/Spinner";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

const PER_PAGE = 12;
const LOGO_FALLBACK = "/uploads/imagens/logo.png"; 

export default function GalleryPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const activeTab = searchParams.get("tab") || "artigos";

  const [loading, setLoading] = useState(true);
  const [artigos, setArtigos] = useState([]);
  const [forumThreads, setForumThreads] = useState([]);
  const [photos, setPhotos] = useState([]);

  const [openIndex, setOpenIndex] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [page, setPage] = useState(1);

  const handleTabChange = (tab) => {
    setPage(1);
    setSearchParams({ tab }); 
  };

  useEffect(() => {
    async function fetchData() {
      try {
        setLoading(true);
        const [resArt, resForum, resPhotos] = await Promise.all([
          api.get("/admin/publications"),
          api.get("/forum/threads"),
          api.get("/galleries")
        ]);
        setArtigos(resArt.data?.data || []);
        setForumThreads(resForum.data?.threads || []);
        setPhotos(resPhotos.data || []);
      } catch (error) {
        console.error("Erro ao carregar dados:", error);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  const handleImgError = (e) => {
    e.target.src = LOGO_FALLBACK;
    e.target.className = "w-full h-full object-contain p-10 bg-gray-50 opacity-50"; 
  };

  if (loading) return <div className="flex justify-center py-20"><Spinner /></div>;

  const totalPages = Math.ceil(photos.length / PER_PAGE);
  const pagePhotos = photos.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  return (
    <div className="max-w-7xl mx-auto px-6 py-10 min-h-screen bg-white">
      
      {/* HEADER & TABS */}
      <div className="flex flex-col md:flex-row justify-between items-center mb-12 gap-6">
        <div>
          <h1 className="text-4xl font-black text-gray-900 tracking-tight">Biblioteca Digital</h1>
          <p className="text-gray-500 text-sm font-medium uppercase tracking-widest">Explore nossa biblioteca</p>
        </div>

        <div className="flex bg-gray-100 p-1.5 rounded-2xl border border-gray-200 shadow-inner">
          {["artigos", "forum", "fotos"].map((tab) => (
            <button
              key={tab}
              onClick={() => handleTabChange(tab)}
              className={`px-6 py-2.5 rounded-xl text-xs font-black transition-all uppercase tracking-widest ${
                activeTab === tab ? "bg-white text-indigo-600 shadow-sm" : "text-gray-400 hover:text-gray-600"
              }`}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>

      {/* GRID DE CONTEÚDO */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {activeTab === "artigos" && artigos.map((item) => (
          <div key={item.id} onClick={() => navigate(`/publicacoes/${item.id}`)} className="group cursor-pointer bg-white rounded-3xl border border-gray-100 shadow-sm hover:shadow-xl transition-all overflow-hidden flex flex-col">
            <div className="h-48 overflow-hidden relative bg-gray-50">
              <img src={item.media?.url || item.url || LOGO_FALLBACK} onError={handleImgError} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" alt={item.title} />
              <div className="absolute top-4 left-4 bg-indigo-600 text-white text-[9px] font-black px-3 py-1 rounded-full uppercase tracking-widest">Artigo</div>
            </div>
            <div className="p-6 flex-1 flex flex-col">
              <h3 className="font-bold text-lg text-gray-800 line-clamp-2 mb-4 group-hover:text-indigo-600 transition-colors">{item.title}</h3>
              <div className="mt-auto flex justify-between items-center text-[10px] font-black text-gray-400 uppercase pt-4 border-t">
                <span>{item.created_at && format(new Date(item.created_at), "dd/MM/yyyy", { locale: ptBR })}</span>
                <span className="text-indigo-500">Acessar →</span>
              </div>
            </div>
          </div>
        ))}

        {activeTab === "forum" && forumThreads.map((thread) => (
          <div key={thread.id} onClick={() => navigate(`/forum?thread=${thread.id}`)} className="group cursor-pointer bg-white rounded-3xl p-6 border-l-4 border-l-indigo-500 border-y border-r border-gray-100 shadow-sm hover:shadow-xl transition-all">
            <div className="w-10 h-10 bg-indigo-50 rounded-xl flex items-center justify-center text-indigo-600 mb-4"><i className="fas fa-comments text-lg"></i></div>
            <h3 className="font-bold text-lg text-gray-800 line-clamp-2 mb-2 group-hover:text-indigo-600 transition-colors">{thread.title}</h3>
            <p className="text-gray-500 text-xs line-clamp-3 italic mb-4 opacity-70">"{thread.content?.substring(0, 100)}..."</p>
            <div className="text-[10px] font-black text-indigo-500 uppercase tracking-widest">Discussão Ativa →</div>
          </div>
        ))}

        {activeTab === "fotos" && pagePhotos.map((p, i) => (
          <div key={p.id} onClick={() => setOpenIndex(i)} className="group relative rounded-2xl overflow-hidden cursor-zoom-in shadow-md transition-all hover:scale-[1.02] bg-gray-100">
            <img src={p.image_url || LOGO_FALLBACK} onError={handleImgError} className="w-full aspect-square object-cover transition-transform duration-700 group-hover:scale-110" alt="" />
            <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition flex items-center justify-center">
               <span className="bg-white/20 backdrop-blur-md px-4 py-2 rounded-full text-white text-[9px] font-black border border-white/20 uppercase">Ampliar</span>
            </div>
          </div>
        ))}
      </div>

      {/* PAGINAÇÃO PARA FOTOS */}
      {activeTab === "fotos" && totalPages > 1 && (
        <div className="flex justify-center gap-2 mt-12">
          {Array.from({ length: totalPages }).map((_, i) => (
            <button key={i} onClick={() => setPage(i + 1)} className={`w-10 h-10 rounded-full font-bold text-xs transition-all ${page === i + 1 ? "bg-indigo-600 text-white shadow-lg" : "bg-gray-100 text-gray-400 hover:bg-gray-200"}`}>{i + 1}</button>
          ))}
        </div>
      )}

   {/* LIGHTBOX CORRIGIDO - Z-INDEX AUMENTADO PARA COBRIR O HEADER */}
      {openIndex !== null && pagePhotos[openIndex] && (
        <div 
          className="fixed inset-0 bg-black/95 backdrop-blur-xl flex flex-col items-center justify-center p-4 animate-fadeIn" 
          style={{ zIndex: 999999 }} // Valor extremamente alto para ficar acima do Header
          onClick={() => { setOpenIndex(null); setZoomLevel(1); }}
        >
          {/* BOTÃO FECHAR - POSICIONADO COM Z-INDEX SUPERIOR */}
          <button 
            className="absolute top-10 right-6 md:top-20 md:right-10 text-white bg-white/10 hover:bg-red-600 w-14 h-14 rounded-full flex items-center justify-center transition-all border border-white/20 shadow-2xl"
            style={{ zIndex: 1000000 }} // Um nível acima do fundo do modal
            onClick={(e) => { 
              e.stopPropagation(); 
              setOpenIndex(null); 
              setZoomLevel(1); 
            }}
          >
            <span className="text-3xl leading-none">✕</span>
          </button>

          {/* Navegação Lateral */}
          <button 
            onClick={(e) => { e.stopPropagation(); setZoomLevel(1); setOpenIndex(i => (i - 1 + pagePhotos.length) % pagePhotos.length); }} 
            className="absolute left-4 md:left-10 text-white text-6xl opacity-30 hover:opacity-100 transition-opacity p-4"
            style={{ zIndex: 1000000 }}
          >
            ‹
          </button>
          <button 
            onClick={(e) => { e.stopPropagation(); setZoomLevel(1); setOpenIndex(i => (i + 1) % pagePhotos.length); }} 
            className="absolute right-4 md:right-10 text-white text-6xl opacity-30 hover:opacity-100 transition-opacity p-4"
            style={{ zIndex: 1000000 }}
          >
            ›
          </button>
          
          {/* Container da Imagem */}
          <div className="relative w-full h-full flex items-center justify-center overflow-auto custom-scrollbar-zoom px-4 py-24">
            <img 
              src={pagePhotos[openIndex].image_url || LOGO_FALLBACK} 
              style={{ 
                transform: `scale(${zoomLevel})`, 
                transition: 'transform 0.2s ease-out',
                maxWidth: '90%',
                maxHeight: '65vh', // Reduzi um pouco para garantir que não bata nos botões
                zIndex: 999999
              }} 
              className="rounded-xl shadow-2xl object-contain origin-center" 
              onClick={(e) => e.stopPropagation()} 
            />
          </div>

          {/* PAINEL DE CONTROLE DE ZOOM */}
          <div 
            className="fixed bottom-10 bg-black/60 backdrop-blur-xl px-10 py-6 rounded-[2.5rem] border border-white/10 w-full max-w-sm flex flex-col gap-3 shadow-2xl animate-scaleUp" 
            style={{ zIndex: 1000000 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between text-[10px] font-black text-white/60 uppercase tracking-widest">
              <span>Min</span>
              <span className="text-indigo-400 text-sm font-bold">{Math.round(zoomLevel * 100)}%</span>
              <span>Max</span>
            </div>
            <input 
              type="range" 
              min="1" 
              max="3" 
              step="0.01" 
              value={zoomLevel} 
              onChange={(e) => setZoomLevel(parseFloat(e.target.value))} 
              className="w-full h-1.5 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-indigo-500" 
            />
            <button 
              onClick={() => setZoomLevel(1)} 
              className="text-[10px] text-white/40 hover:text-white uppercase font-bold mt-1 transition-colors"
            >
              Resetar Escala
            </button>
          </div>
        </div>
      )}

      <style>{`
        .custom-scrollbar-zoom::-webkit-scrollbar { width: 4px; height: 4px; }
        .custom-scrollbar-zoom::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.2); border-radius: 10px; }
        input[type='range']::-webkit-slider-thumb { appearance: none; width: 18px; height: 18px; background: #6366f1; border-radius: 50%; border: 3px solid white; cursor: pointer; box-shadow: 0 0 10px rgba(99, 102, 241, 0.4); }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes scaleUp { from { opacity: 0; transform: scale(0.9); } to { opacity: 1; transform: scale(1); } }
      `}</style>
    </div>
  );
}